import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, Home, ChevronRight, Truck, Clock, MapPin, Phone, BookOpen } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { LOCATIONS, PRODUCTS, SHOP_URL, CATALOG_URL, CONTACT } from '../mock';
import { trackCallClick } from '../lib/analytics';
import Seo, { SITE } from '../components/Seo';
import Reveal from '../components/Reveal';

function ProductCard({ p }) {
  return (
    <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-[#EFE8DD] hover:shadow-[0_20px_50px_rgba(61,81,66,0.15)] hover:-translate-y-1.5 transition-all duration-500">
      <div className="relative overflow-hidden aspect-square bg-[#F4EFE7]">
        <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        {p.tags.includes('Best seller') && (
          <span className="absolute top-3 left-3 bg-[#C4715A] text-white text-[11px] font-medium tracking-wide px-3 py-1 rounded-full">Best Seller</span>
        )}
        <span className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-md">
          <ArrowUpRight className="w-4.5 h-4.5 text-[#3D5142]" />
        </span>
      </div>
      <div className="p-4 flex items-center justify-between gap-2">
        <div>
          <h3 className="font-serif-display text-[17px] text-[#2A2A28] leading-tight">{p.name}</h3>
          <p className="text-[12px] text-[#8A7C6A] mt-0.5">Next-day delivery</p>
        </div>
        <span className="font-medium text-[16px] text-[#3D5142] whitespace-nowrap">${p.price}</span>
      </div>
    </a>
  );
}

export default function LocationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const loc = LOCATIONS.find((l) => l.slug === slug);

  if (!loc) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif-display text-[40px] text-[#26302a]">Area not found</h1>
        <button onClick={() => navigate('/')} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#3D5142] text-white px-6 py-3"><ArrowLeft className="w-4 h-4" /> Back home</button>
      </div>
    );
  }

  const products = PRODUCTS.filter((p) => p.tags.includes('Best seller')).concat(PRODUCTS.slice(0, 8)).filter((v, i, a) => a.indexOf(v) === i).slice(0, 8);
  const url = `${SITE}/flower-delivery/${loc.slug}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Florist',
      name: `Delivery Florist | ${loc.city}`,
      description: loc.metaDescription,
      url,
      image: loc.heroImg,
      telephone: '+1-650-304-4773',
      email: CONTACT.email,
      priceRange: '$$',
      address: { '@type': 'PostalAddress', addressLocality: loc.city, addressRegion: 'CA', postalCode: loc.zip, addressCountry: 'US' },
      geo: { '@type': 'GeoCoordinates', latitude: loc.lat, longitude: loc.lng },
      openingHoursSpecification: [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], opens: '09:00', closes: '17:00' }],
      areaServed: { '@type': 'City', name: loc.city },
      makesOffer: { '@type': 'Offer', itemOffered: { '@type': 'Service', name: `Same-Day Flower Delivery in ${loc.city} (${loc.zip})`, serviceType: 'Same-day flower delivery' } },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: `Flower Delivery in ${loc.city}`, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: loc.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    },
  ];

  return (
    <>
      <Seo title={loc.title} description={loc.metaDescription} path={`/flower-delivery/${loc.slug}`} image={loc.heroImg} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={loc.heroImg} alt={`Flower delivery in ${loc.city}`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#26302a]/90 via-[#26302a]/70 to-[#26302a]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <nav className="flex items-center gap-2 text-[13px] text-[#DCE3D6] mb-6">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-white transition-colors"><Home className="w-3.5 h-3.5" /> Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white">{loc.city} ({loc.zip})</span>
          </nav>
          <span className="text-[13px] tracking-[0.3em] uppercase text-[#D9A79C] font-medium">{loc.tagline}</span>
          <h1 className="font-serif-display text-[38px] sm:text-[52px] text-white mt-3 leading-[1.05] max-w-3xl">{loc.h1}</h1>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-[#E4E9DE]">
            <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-[#D9A79C]" /> Same-day delivery</span>
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#D9A79C]" /> Order before 2 PM</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D9A79C]" /> ZIP {loc.zip}</span>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white text-[#3D5142] hover:bg-[#F1ECE3] text-[16px] font-medium px-7 py-3.5 transition-colors shadow-lg">Order Now <ArrowUpRight className="w-4.5 h-4.5" /></a>
            <a href={CONTACT.phoneHref} onClick={() => trackCallClick('location_page')} className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white hover:bg-white hover:text-[#3D5142] text-[16px] font-medium px-7 py-3.5 transition-colors"><Phone className="w-4 h-4" /> {CONTACT.phone}</a>
          </div>
        </div>
      </section>

      {/* Intro + neighborhoods */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Reveal>
            {loc.intro.map((t, i) => (
              <p key={i} className="text-[17px] leading-[1.8] text-[#4A4A45] mb-5">{t}</p>
            ))}
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-6 rounded-2xl bg-[#F1ECE3] p-7">
              <h2 className="font-serif-display text-[24px] text-[#26302a] mb-4">Neighborhoods we deliver to in {loc.city}</h2>
              <div className="flex flex-wrap gap-2.5">
                {loc.neighborhoods.map((n) => (
                  <span key={n} className="inline-flex items-center gap-1.5 bg-white border border-[#E5DCCF] rounded-full px-4 py-2 text-[14px] text-[#4A4A45]"><MapPin className="w-3.5 h-3.5 text-[#C4715A]" /> {n}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Products */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-serif-display text-[30px] sm:text-[38px] text-[#26302a] leading-tight mb-8">Popular bouquets to send in {loc.city}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 80}><ProductCard p={p} /></Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href={CATALOG_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#3D5142] text-[#3D5142] hover:bg-[#3D5142] hover:text-white text-[16px] font-medium px-8 py-3.5 transition-colors">See all flowers <ArrowUpRight className="w-4.5 h-4.5" /></a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-serif-display text-[30px] sm:text-[38px] text-[#26302a] leading-tight mb-6">Same-day delivery in {loc.city}: FAQs</h2>
          </Reveal>
          <Reveal delay={60}>
            <Accordion type="single" collapsible className="space-y-3">
              {loc.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-[#EFE8DD] px-5 overflow-hidden">
                  <AccordionTrigger className="text-left font-serif-display text-[18px] text-[#2A2A28] hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[15.5px] leading-relaxed text-[#5A554C] pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          {loc.blogSlug && (
            <Reveal delay={100}>
              <Link to={`/blog/${loc.blogSlug}`} className="mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-[#3D5142] hover:text-[#C4715A] transition-colors">
                <BookOpen className="w-4 h-4" /> Read our local guide to flower delivery in {loc.city}
              </Link>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
